import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, ConfirmDialog, Flex, Text, Tooltip } from 'core/components';
import { hasFilters } from 'core/util/filters';
import { UdeFormProps } from 'app/views/boards/panels/ude/form.types';
import { TbChevronsLeft, TbHistory, TbX } from 'react-icons/tb';
import { useDictionaryMeasurements } from 'app/stores/dictionary/useDictionaryMeasurements';
import BoardPanelModel from 'app/stores/boards/BoardPanelModel';
import { getMissingGuidedVariableMappings } from 'app/stores/boards/guidedVariableMappings';
import { checkMeasurement } from '@kentik/ui-shared/query/measurements';
import { isNonSpecializingDimension } from '@kentik/ui-shared/query/dimensionUtils';
import { useQueryHistory } from './history/useQueryHistory';
import { useUdeNlq } from './ai/useUdeNlq';
import CategoryPanel from './CategoryPanel';
import MeasurementBreadcrumb from './components/MeasurementBreadcrumb';
import SelectedPanel from './SelectedPanel';
import UdeQueryHistoryList from './history/UdeQueryHistoryList';
import SidebarSection from '../../SidebarSection';
import UdeFilterModal from './filters/UdeFilterModal';
import { UdeFilterFormDisplay } from './filters/UdeFilterDisplay';
import BoardVariablesContext, { AdvancedVariableMapping } from './filters/BoardVariablesContext';
import PanelVariableMapping from '../../PanelVariableMapping';
import { useAutoApply } from './useAutoApply';
import { useAutoApplyEffect } from './useAutoApplyEffect';
import { useConfirmableAction } from './useConfirmableAction';
import { useMeasurementNavigation } from './useMeasurementNavigation';
import { getCategoryPrefix } from './categoryUtils';
import { getQueryFieldsSignature } from './queryFieldsSignature';
import PopoverGateContext, { PopoverGateSource, PopoverGateState } from './PopoverGateContext';

const UdePanelForm = (
  props: UdeFormProps & {
    panel: BoardPanelModel;
    onToggleCollapse?: () => void;
    queryResetSignal?: number;
    discardSignal?: number;
    onReset?: () => void;
  }
) => {
  const { form, onRunQuery, onSave, panel, onToggleCollapse, queryResetSignal, discardSignal, onReset } = props;
  const [autoApply] = useAutoApply();
  const nlq = useUdeNlq();
  const handleAutoTitle = useCallback(
    (title: string) => {
      form.setValue('title', title as never);
      // Persist title as draft — does not navigate (handleSavePanel guards on !options.draft)
      onSave({ title }, { draft: true, toast: false });
    },
    [form, onSave]
  );
  const history = useQueryHistory({
    panel,
    summarize: nlq.summarize,
    generateTitle: nlq.generateTitle,
    onAutoTitle: handleAutoTitle,
    discardSignal
  });
  const formValues = form.getValues();
  const measurementName = form.getValue('config.data.measurement');
  const metrics = form.getValue('config.data.metrics') || [];
  const dimensions = form.getValue('config.data.dimensions') || [];

  const { measurements, measurementHash, appProtocols } = useDictionaryMeasurements();

  // Filter modal state (declared before queryFieldsKey so we can gate filter tracking)
  const [isEditingFilters, setIsEditingFilters] = useState(false);
  const [openPopovers, setOpenPopovers] = useState<PopoverGateState>({
    metrics: false,
    dimensions: false
  });
  const isPopoverOpen = openPopovers.metrics || openPopovers.dimensions;

  const handlePopoverToggle = useCallback((source: PopoverGateSource, isOpen: boolean) => {
    setOpenPopovers((current) => (current[source] === isOpen ? current : { ...current, [source]: isOpen }));
  }, []);

  const popoverGateValue = useMemo(
    () => ({ openPopovers, setPopoverOpen: handlePopoverToggle }),
    [openPopovers, handlePopoverToggle]
  );

  const queryFieldsKey = getQueryFieldsSignature(form.getValues().config?.data);

  // Auto-apply: debounced query execution when query fields change and no gate is open
  useAutoApplyEffect({
    autoApply,
    measurementName,
    metricsCount: metrics.length,
    isEditingFilters,
    isPopoverOpen,
    queryFieldsKey,
    onRun: () => onRunQuery?.()
  });

  const handleToggleFilterModal = useCallback(() => {
    setIsEditingFilters((prev) => !prev);
  }, []);

  const { categoryPrefix, breadcrumbProps, resetQueryFields, applyCategory, applyMeasurement, handleSearchSelect } =
    useMeasurementNavigation({ form, measurements, measurementName, queryResetSignal, discardSignal });

  const applyBackToCategories = useCallback(() => {
    onReset?.();
  }, [onReset]);

  useEffect(() => {
    if (checkMeasurement.isTraffic(measurementName)) {
      // Collect dimension names from the query's top-level selected dimensions
      type RawDimension = { name?: string };
      const queryDimNames: string[] = (
        (formValues as { config?: { data?: { dimensions?: RawDimension[] } } })?.config?.data?.dimensions ?? []
      )
        .map((d) => d.name)
        .filter((n): n is string => Boolean(n));

      // Recursively collect filterField names from nested dimension filter groups
      const filterDimNames: string[] = [];
      type RawFilterGroup = { filters?: { filterField?: string }[]; filterGroups?: RawFilterGroup[] };
      const collectFilterFields = (group: RawFilterGroup | null | undefined): void => {
        if (group) {
          for (const f of group.filters ?? []) {
            if (f.filterField) {
              filterDimNames.push(f.filterField);
            }
          }
          for (const nested of group.filterGroups ?? []) {
            collectFilterFields(nested);
          }
        }
      };
      const filtersDimensions = (formValues as { config?: { data?: { filters?: { dimensions?: RawFilterGroup } } } })
        ?.config?.data?.filters?.dimensions;
      collectFilterFields(filtersDimensions);

      const allDimNames = [...queryDimNames, ...filterDimNames];

      // Find the most specific traffic measurement that contains any of the collected dimension names
      // If we find one that is not /traffic, we will set it and be done.
      for (const dimName of allDimNames) {
        if (!isNonSpecializingDimension(dimName)) {
          for (const m of measurements) {
            const { measurement: currMeasurementName } = m;
            if (checkMeasurement.isTrafficChild(currMeasurementName) && dimName in (m.storage?.Dimensions ?? {})) {
              if (measurementName !== currMeasurementName) {
                form.setValue('config.data.measurement', currMeasurementName);
              }
              return;
            }
          }
        }
      }

      // If we got here and we weren't already set to /traffic, let's do that.
      if (!checkMeasurement.isTrafficRoot(measurementName)) {
        form.setValue('config.data.measurement', '/traffic');
      }
    }
  }, [form, formValues, measurementName, measurements]);

  const boundMeasurementRef = useRef<string | null | undefined>(measurementName);
  useEffect(() => {
    if (boundMeasurementRef.current === measurementName) {
      return;
    }

    boundMeasurementRef.current = measurementName;
    if (!measurementName) {
      return;
    }

    const boardVariables = panel.board.get('config').variables ?? [];
    if (boardVariables.length === 0) {
      return;
    }

    const mappedNames = new Set((panel.get('config').variableMappings ?? []).map((m) => m.variableName));
    const missing = getMissingGuidedVariableMappings(boardVariables, mappedNames, measurementName, measurements);
    if (missing.length === 0) {
      return;
    }

    missing.forEach((mapping) => panel.setVariableMapping(mapping));
    form.setValuesDeep({ config: { variableMappings: panel.get('config').variableMappings } });
    form.onChange({ form, formValues: form.getValues() });
  }, [measurementName, panel, measurements, form]);

  const { request: requestCategoryChange, ...categoryChangeConfirm } = useConfirmableAction<string>({
    onApply: applyCategory
  });

  const { request: requestMeasurementChange, ...measurementChangeConfirm } = useConfirmableAction<string>({
    onApply: applyMeasurement
  });

  const { request: requestReset, ...resetConfirm } = useConfirmableAction<void>({
    onApply: applyBackToCategories
  });

  /**
   * Entry point for the header breadcrumb. Switching measurement resets the current
   * query, so when the user has existing metric/dimension selections we confirm
   * first to avoid silent data loss — mirroring the category-switch guard.
   */
  const handleMeasurementSelect = useCallback(
    (measurement: string) => {
      const hasSelections = metrics.length > 0 || dimensions.length > 0;
      if (hasSelections) {
        requestMeasurementChange(measurement);
        return;
      }
      applyMeasurement(measurement);
    },
    [metrics.length, dimensions.length, applyMeasurement, requestMeasurementChange]
  );

  // Returning to the category picker clears the entire query. Warn first when the
  // user has a query in progress so the reset isn't a surprise; otherwise go back
  // immediately.
  const handleBackToCategories = useCallback(() => {
    if (!onReset) {
      return;
    }
    const currentMetrics = form.getValue('config.data.metrics') || [];
    const currentDimensions = form.getValue('config.data.dimensions') || [];
    const dimensionFilterGroups = form.getValue('config.data.filters.dimensions.filterGroups') || [];
    const hasQuery =
      currentMetrics.length > 0 ||
      currentDimensions.length > 0 ||
      !!hasFilters({ filterGroups: dimensionFilterGroups });
    if (hasQuery) {
      requestReset();
    } else {
      onReset();
    }
  }, [onReset, form, requestReset]);

  const handleCategorySelect = useCallback(
    (prefix: string) => {
      const hasSelections = metrics.length > 0 || dimensions.length > 0;
      let currentTopLevel: string | null;
      if (measurementName) {
        currentTopLevel = getCategoryPrefix(measurementName);
      } else if (categoryPrefix) {
        currentTopLevel = getCategoryPrefix(categoryPrefix);
      } else {
        currentTopLevel = null;
      }
      const incomingTopLevel = getCategoryPrefix(prefix);

      // If switching top-level category with existing selections, confirm first
      if (currentTopLevel && hasSelections && currentTopLevel !== incomingTopLevel) {
        requestCategoryChange(prefix);
        return;
      }

      applyCategory(prefix);
    },
    [metrics.length, dimensions.length, measurementName, categoryPrefix, applyCategory, requestCategoryChange]
  );

  const handleSelectHistoryEntry = useCallback(
    (index: number) => {
      if (!history) {
        return;
      }
      const query = history.goToEntry(index);
      if (query) {
        // Restore all query fields, clearing any that are absent in the snapshot
        form.setValue('config.data.measurement', (query.measurement ?? '') as never);
        form.setValue('config.data.metrics', (query.metrics ?? []) as never);
        form.setValue('config.data.dimensions', (query.dimensions ?? []) as never);
        form.setValue('config.data.time', (query.time ?? {}) as never);
        form.setValue('config.data.filters', (query.filters ?? {}) as never);
        form.setValue('config.data.rollups', (query.rollups ?? []) as never);
        form.setValue('config.data.sort', (query.sort ?? []) as never);

        // Restore chart config if present
        const entry = history.entries[index];
        if (entry?.chart) {
          form.setValuesDeep({ config: { chart: entry.chart } } as never);
        }

        history.toggleOpen(); // Close history panel
        onRunQuery?.(); // Execute the restored query
      }
    },
    [history, form, onRunQuery]
  );

  const handleRemoveEntry = useCallback(
    (index: number) => {
      history?.removeEntry(index);
    },
    [history]
  );

  const handleSetToHead = useCallback(
    (index: number) => {
      if (!history) {
        return;
      }
      const query = history.setToHead(index);
      if (query) {
        // Restore all query fields, clearing any that are absent in the snapshot
        form.setValue('config.data.measurement', (query.measurement ?? '') as never);
        form.setValue('config.data.metrics', (query.metrics ?? []) as never);
        form.setValue('config.data.dimensions', (query.dimensions ?? []) as never);
        form.setValue('config.data.time', (query.time ?? {}) as never);
        form.setValue('config.data.filters', (query.filters ?? {}) as never);
        form.setValue('config.data.rollups', (query.rollups ?? []) as never);
        form.setValue('config.data.sort', (query.sort ?? []) as never);

        const headEntry = history.entries[0];
        if (headEntry?.chart) {
          form.setValuesDeep({ config: { chart: headEntry.chart } } as never);
        }

        onRunQuery?.();
      }
    },
    [history, form, onRunQuery]
  );

  // Check if dimension filters exist to toggle "Add" vs "Edit" button text
  const dimensionFilterGroups = form.getValue('config.data.filters.dimensions.filterGroups') || [];
  const hasDimensionFilters =
    Array.isArray(dimensionFilterGroups) &&
    dimensionFilterGroups.length > 0 &&
    dimensionFilterGroups.some((group: { filters?: unknown[] }) => (group.filters?.length ?? 0) > 0);

  const boardVariables = panel.board.get('config')?.variables ?? [];

  // All enabled board variables are available as {{variableName}} substitution options in filter
  // value inputs — both custom and guided. Reading board observable directly so MobX re-renders on change.
  const advancedMappings: AdvancedVariableMapping[] = boardVariables
    .filter((v) => v.enabled !== false)
    .map((v) => ({ variableName: v.name }));

  return (
    <PopoverGateContext.Provider value={popoverGateValue}>
      <Flex className="UdePanelForm" flexDirection="column" flex={1} style={{ minHeight: 0 }}>
        {!categoryPrefix ? (
          <CategoryPanel
            selectedCategory={categoryPrefix}
            onSelectCategory={handleCategorySelect}
            measurements={measurements}
            onSearchSelect={handleSearchSelect}
            onToggleCollapse={onToggleCollapse}
          />
        ) : null}

        {categoryPrefix && (
          <SidebarSection
            defaultIsOpen
            className={`ude-measurement-header ${history?.isOpen ? '' : 'ude-query-header'}`}
            isCollapsible={false}
            borderBottom="none"
            iconProps={{ color: 'kentik' }}
            pl="8px"
            pr="4px"
            title={
              <Flex flex={1} minWidth={0} overflow="hidden" alignItems="center">
                <Text as="div" fontSize={14} fontWeight="bold">
                  {history?.isOpen ? 'Query History' : 'Query'}
                </Text>
              </Flex>
            }
            rightElement={
              <Flex gap="4px" ml="auto" alignItems="center">
                {history?.isOpen ? (
                  <Button minimal icon={TbX} onClick={history.toggleOpen} aria-label="Close history" />
                ) : (
                  <>
                    {history && history.entries.length > 0 && (
                      <Tooltip content="Query history">
                        <Button minimal icon={TbHistory} onClick={history.toggleOpen} aria-label="Query history" />
                      </Tooltip>
                    )}
                    {onToggleCollapse && (
                      <Tooltip content="Close Query options">
                        <Button
                          minimal
                          icon={TbChevronsLeft}
                          onClick={onToggleCollapse}
                          aria-label="Close Query options"
                        />
                      </Tooltip>
                    )}
                  </>
                )}
              </Flex>
            }
          >
            {!history?.isOpen && (
              <Flex
                bg="sectionBackground"
                p="8px 12px 8px 8px"
                mb={2}
                borderBottom="thin"
                alignItems="center"
                minWidth={0}
                overflow="hidden"
              >
                <MeasurementBreadcrumb
                  {...breadcrumbProps}
                  onSelectMeasurement={handleMeasurementSelect}
                  onReset={onReset ? handleBackToCategories : undefined}
                />
              </Flex>
            )}
            {history?.isOpen ? (
              <UdeQueryHistoryList
                entries={history.entries}
                headIndex={history.headIndex}
                onSelectEntry={handleSelectHistoryEntry}
                onRemoveEntry={handleRemoveEntry}
                onSetToHead={handleSetToHead}
                onClose={history.toggleOpen}
                hideHeader
              />
            ) : (
              measurementName && (
                <SelectedPanel
                  form={form}
                  measurements={measurements}
                  measurementHash={measurementHash}
                  appProtocols={appProtocols}
                  onReset={resetQueryFields}
                />
              )
            )}
          </SidebarSection>
        )}

        {categoryPrefix && measurementName && !history?.isOpen && (
          <BoardVariablesContext.Provider value={advancedMappings}>
            <SidebarSection
              defaultIsOpen
              isCollapsible={false}
              borderBottom="none"
              iconProps={{ color: 'kentik' }}
              pr={1}
              title={
                <Text fontSize={13} fontWeight="bold">
                  FILTERS
                </Text>
              }
              rightElement={
                <Button
                  small
                  minimal
                  intent="primary"
                  text={hasDimensionFilters ? 'Edit' : 'Add'}
                  onClick={handleToggleFilterModal}
                  ml="auto"
                />
              }
            >
              <Flex flexDirection="column" px="12px" pb="8px" gap={1}>
                <UdeFilterFormDisplay type="dimensions" measurementName={measurementName} />
                <UdeFilterModal
                  title="Filters"
                  fieldName="config.data.filters.dimensions"
                  isOpen={isEditingFilters}
                  onClose={handleToggleFilterModal}
                  measurement={measurementName}
                  measurements={measurements}
                  measurementHash={measurementHash}
                />
              </Flex>
            </SidebarSection>
          </BoardVariablesContext.Provider>
        )}

        {categoryPrefix && measurementName && boardVariables.length > 0 && (
          <SidebarSection
            defaultIsOpen
            isCollapsible={false}
            borderBottom="none"
            iconProps={{ color: 'kentik' }}
            pr={1}
            title={
              <Text fontSize={13} fontWeight="bold">
                FILTERS FROM DASHBOARD
              </Text>
            }
          >
            <Flex flexDirection="column" px="12px" pb="8px" gap="2px">
              {boardVariables.map((bv) => (
                <PanelVariableMapping
                  key={bv.name}
                  form={form}
                  measurements={measurements}
                  panel={panel}
                  variable={bv}
                />
              ))}
            </Flex>
          </SidebarSection>
        )}

        <ConfirmDialog
          isOpen={categoryChangeConfirm.isOpen}
          title="Change Category?"
          text="Changing the category will clear your current selections. Continue?"
          confirmButtonText="Change Category"
          confirmButtonIntent="danger"
          onConfirm={categoryChangeConfirm.confirm}
          onCancel={categoryChangeConfirm.cancel}
        />

        <ConfirmDialog
          isOpen={measurementChangeConfirm.isOpen}
          title="Change Measurement?"
          text="Changing the measurement will clear your current selections. Continue?"
          confirmButtonText="Change Measurement"
          confirmButtonIntent="danger"
          onConfirm={measurementChangeConfirm.confirm}
          onCancel={measurementChangeConfirm.cancel}
        />

        <ConfirmDialog
          isOpen={resetConfirm.isOpen}
          title="Clear query?"
          text="Going back to categories will clear your current query. Continue?"
          confirmButtonText="Clear query"
          confirmButtonIntent="danger"
          onConfirm={resetConfirm.confirm}
          onCancel={resetConfirm.cancel}
        />
      </Flex>
    </PopoverGateContext.Provider>
  );
};

UdePanelForm.displayName = 'UdePanelForm';

export default observer(UdePanelForm);
